import { useState, useEffect, useCallback } from "react";
import supabase from "../../../../utils/supabase";

interface Class {
  id: string;
  name: string;
  description?: string;
  semester: string;
  join_code?: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  settings: {
    class_time: string;
    reflection_requirements?: {
      min_words?: number;
      min_sentences?: number;
      enabled: boolean;
    };
  };
  grade_type: "ABC" | "1234" | "%";
  student_count?: number;
  google_class_id?: string;
  last_synced?: string;
  is_archived?: boolean;
}

interface UseClassesProps {
  user: any;
  searchTerm: string;
  currentPage: number;
  limit: number;
}

export function useClasses({
  user,
  searchTerm,
  currentPage,
  limit,
}: UseClassesProps) {
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalClasses, setTotalClasses] = useState(0);

  const fetchTeacherClasses = useCallback(
    async (searchValue?: string) => {
      if (!user?.id) return;

      setLoading(true);
      try {
        let query = supabase
          .from("classes")
          .select("*", { count: "exact" })
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        // Add search filter if searchTerm is provided
        const searchToUse =
          searchValue !== undefined ? searchValue : searchTerm;
        if (searchToUse.trim()) {
          query = query.ilike("name", `%${searchToUse.trim()}%`);
        }

        // Add pagination
        const from = (currentPage - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) {
          console.error("Error fetching classes:", error);
          return;
        }

        // Transform data to ensure required fields have default values
        const transformedData = (data || []).map((cls: any) => ({
          ...cls,
          settings: cls.settings || {
            class_time: "Not specified",
          },
          grade_type: cls.grade_type || "ABC",
        }));

        setClasses(transformedData);
        setTotalClasses(count || 0);
      } catch (error) {
        console.error("Error fetching classes:", error);
      } finally {
        setLoading(false);
      }
    },
    [user?.id, searchTerm, currentPage, limit]
  );

  // Combined effect for search and pagination
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchTeacherClasses();
      },
      searchTerm === "" ? 0 : 500
    );

    return () => clearTimeout(timeoutId);
  }, [searchTerm, currentPage, fetchTeacherClasses]);

  return {
    classes,
    loading,
    totalClasses,
    fetchTeacherClasses,
  };
}
